import { PLAN_CATALOG } from '@secretario/shared';
import { Link } from 'wouter';
import { MesaIcon, type MesaIconName } from '../clinica/mesa/MesaIcon.js';
import './desk-side.css';

type Place = { href: string; label: string; icon: MesaIconName; match: (path: string) => boolean };
const starts = (prefix: string) => (path: string) =>
  path === prefix || path.startsWith(`${prefix}/`);

/** Os lugares do app pessoal na mesa: os quatro da barra do celular e o resto do que ele faz. */
const MAIN: Place[] = [
  {
    href: '/',
    label: 'Tela inicial',
    icon: 'inicio',
    match: (p) => p === '/' || p.startsWith('/habilidade'),
  },
  { href: '/conversas', label: 'Conversas', icon: 'mensagem', match: starts('/conversas') },
  { href: '/contatos', label: 'Contatos', icon: 'pessoa', match: starts('/contatos') },
  { href: '/agenda', label: 'Agenda', icon: 'agenda', match: starts('/agenda') },
];
const MORE: Place[] = [
  { href: '/entregas', label: 'Documentos', icon: 'prontuario', match: starts('/entregas') },
  { href: '/servicos', label: 'Dinheiro e serviços', icon: 'dinheiro', match: starts('/servicos') },
  { href: '/saude', label: 'Saúde', icon: 'haltere', match: starts('/saude') },
  { href: '/objetivos', label: 'Objetivos', icon: 'estrela', match: starts('/objetivos') },
  { href: '/receitas', label: 'Receitas', icon: 'prato', match: starts('/receitas') },
  { href: '/perto', label: 'Perto de Mim', icon: 'busca', match: starts('/perto') },
];
const BUSINESS: Place = {
  href: '/negocio',
  label: 'Meu negócio',
  icon: 'funil',
  match: starts('/negocio'),
};
const SETTINGS: Place = {
  href: '/ajustes',
  label: 'Ajustes',
  icon: 'ajustes',
  match: starts('/ajustes'),
};

/**
 * A mesa do app pessoal (1024 px para cima, leva 1): a mesma barra lateral da clínica no
 * lugar da barra de baixo do celular, com o botão de falar com o Secretário no pé.
 */
export function DeskSide({
  location,
  unread,
  isPro,
  onTalk,
}: {
  location: string;
  unread: number;
  isPro: boolean;
  onTalk: () => void;
}) {
  const row = (place: Place) => {
    const on = place.match(location);
    return (
      <Link
        key={place.href}
        href={place.href}
        className={on ? 'on' : undefined}
        aria-current={on ? 'page' : undefined}
      >
        <MesaIcon name={place.icon} />
        <span>{place.label}</span>
        {place.href === '/conversas' && unread > 0 && (
          <em className="ds-badge" aria-label={`${unread} não lidas`}>
            {unread > 99 ? '99+' : unread}
          </em>
        )}
      </Link>
    );
  };
  return (
    <aside className="ds" aria-label="Navegação">
      <Link href="/" className="ds-brand">
        <img src="/icon-app.png" alt="" />
        <span>
          <b>Secretário</b>
          {isPro && <small>{PLAN_CATALOG.pro.name}</small>}
        </span>
      </Link>
      <nav className="ds-nav">{MAIN.map(row)}</nav>
      <nav className="ds-nav" aria-label="Mais lugares">
        {MORE.map(row)}
      </nav>
      <nav className="ds-nav" aria-label="Negócio e conta">
        {row(BUSINESS)}
        {row(SETTINGS)}
      </nav>
      <button type="button" className="ds-talk" onClick={onTalk}>
        <MesaIcon name="mensagem" size={18} />
        Falar com o Secretário
      </button>
    </aside>
  );
}
